import React, { useState, useEffect } from 'react';
import {
  Users, ArrowUpRight, ArrowDownRight, Repeat, Activity,
  Server, BarChart3, DollarSign, Zap, ShieldAlert,
} from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';
import KPITile from '../../components/KPITile';
import ActivityFeed from '../../components/ActivityFeed';

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get('/admin/dashboard').then(r => setData(r.data)).catch(() => {});
    load().finally(() => setLoading(false));

    const sock = getSocket();
    if (sock) sock.on('trade:opened', load);

    const interval = setInterval(load, 15000);
    return () => {
      clearInterval(interval);
      if (sock) sock.off('trade:opened', load);
    };
  }, []);

  if (loading) return <p style={{color:'var(--text-secondary)'}}>Loading dashboard…</p>;
  if (!data)   return <p style={{color:'var(--loss)'}}>Failed to load dashboard.</p>;

  const s = data.stats;
  // Absent on an older backend — the tile is simply not rendered then, so the
  // two deploys can land in either order.
  const gate = data.provisionGate;
  const pnl = s.todayPnl ?? 0;
  const pnlIntent = pnl > 0 ? 'profit' : pnl < 0 ? 'loss' : 'neutral';
  const pnlTrend  = pnl > 0 ? 'up'     : pnl < 0 ? 'down' : 'flat';
  const fmt = n => Number(n || 0).toLocaleString();

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>Overview</h1>
          <div className="subtitle">Live platform health and recent copy activity</div>
        </div>
      </div>

      {/* KPI grid. auto-fit rather than fixed columns so the fifth tile has
          somewhere to go — the gate tile is only rendered by a backend that
          reports it, so the count varies. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 20,
        marginBottom: 24,
      }}>
        <KPITile label="Total Users" value={fmt(s.totalUsers)}
          sub={`${s.traders} traders · ${s.copiers} copiers`}
          icon={<Users size={16} />} intent="neutral" />
        <KPITile label="Running Containers" value={fmt(s.runningContainers)}
          sub="MT5 instances online" icon={<Server size={16} />} intent="neutral" />
        <KPITile label="Open Trades" value={fmt(s.activeTrades)}
          sub={`${fmt(s.totalCopyExecutions)} total copies`}
          icon={<Activity size={16} />} intent="neutral" />
        <KPITile label="Today P&L"
          value={`${pnl >= 0 ? '+' : ''}$${Number(pnl).toFixed(2)}`}
          sub={pnl >= 0 ? 'In profit' : 'In loss'}
          trend={pnlTrend}
          icon={<DollarSign size={16} />}
          intent={pnlIntent} />
        {gate && <ProvisionGateTile gate={gate} />}
      </div>

      <div className="row">
        <div className="col-lg-8 mb-4">
          <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <h5 className="card-title">Recent Copy Activity</h5>
                <div className="card-category" style={{marginTop:4}}>Last 30 executions</div>
              </div>
              <BarChart3 size={16} color="var(--text-tertiary)" />
            </div>
            <ActivityFeed items={(data.recentActivity || []).slice(0, 30)} />
          </div>
        </div>

        <div className="col-lg-4 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Quick Stats</h5>
              <div className="card-category" style={{marginTop:4}}>This week</div>
            </div>
            <div className="card-body" style={{padding:0}}>
              <QuickStat label="Active Traders"  value={fmt(s.traders)}  icon={<ArrowUpRight size={14} />} />
              <QuickStat label="Active Copiers"  value={fmt(s.copiers)}  icon={<Repeat size={14} />} />
              <QuickStat label="Running"         value={fmt(s.runningContainers)} icon={<Server size={14} />} />
              <QuickStat label="Total Copies"    value={fmt(s.totalCopyExecutions)}
                icon={<ArrowDownRight size={14} />} last />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Auto-provision capacity gate.
 *
 * The number an admin actually needs is "how many more accounts start
 * themselves before I am back in the loop" — so the tile leads with used/limit
 * and says what the CURRENT state means for them. While it is auto-provisioning
 * nothing reaches the verify queue, which is the difference between an empty
 * queue meaning "no work" and meaning "the alerting broke".
 */
function ProvisionGateTile({ gate }) {
  const off  = !gate.limit;
  const full = !off && !gate.autoVerifying;
  const slots = gate.remaining === 1 ? 'slot' : 'slots';
  return (
    <KPITile
      label="Auto-Provisioning"
      value={off ? 'Manual' : `${gate.used} / ${gate.limit}`}
      sub={off
        ? 'Disabled — every account waits for a verify'
        : full
          ? 'Limit reached — new accounts need verifying'
          : `${gate.remaining} ${slots} left — new accounts start themselves`}
      icon={off || full ? <ShieldAlert size={16} /> : <Zap size={16} />}
      intent={off || full ? 'warning' : 'neutral'} />
  );
}

function QuickStat({ label, value, icon, last }) {
  return (
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'14px 20px',
      borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
    }}>
      <span style={{display:'inline-flex', alignItems:'center', gap:10, color:'var(--text-secondary)', fontSize:13}}>
        <span style={{
          width:24, height:24, borderRadius:6, background:'var(--bg-surface-3)',
          display:'inline-flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)',
        }}>{icon}</span>
        {label}
      </span>
      <span className="num" style={{fontWeight:600, fontSize:15, color:'var(--text-primary)'}}>{value}</span>
    </div>
  );
}
